import { IkeGeodesic } from '@ikegps/core-measurement';
import { action, computed, observable, makeObservable } from 'mobx';

export interface MeasurementPoint {
	/** Latitude and longitude */
	location: google.maps.LatLng;
	/** Type of point: 'pole' for collection, 'arbitrary' for user-clicked point */
	type: 'pole' | 'arbitrary';
	/** Collection revision if this is a pole location */
	collectionRevision?: string;
	/** Unique ID for this point */
	id: string;
}

export class MeasurementState {
	@observable points: MeasurementPoint[] = [];
	@observable isActive: boolean = false;
	@observable isPaused: boolean = false;

	constructor() {
		makeObservable(this);
	}

	@action.bound
	setActive(active: boolean) {
		this.isActive = active;
		if (!active) {
			this.isPaused = false;
		}
	}

	@action.bound
	setPaused(paused: boolean) {
		this.isPaused = paused;
	}

	@action.bound
	addPoint(point: MeasurementPoint) {
		this.points.push(point);
	}

	@action.bound
	updatePointLocation(id: string, location: google.maps.LatLng) {
		const point = this.points.find((p) => p.id === id);
		if (point) {
			point.location = location;
		}
	}

	@action.bound
	removePoint(pointId: string) {
		const index = this.points.findIndex((p) => p.id === pointId);
		if (index !== -1) {
			this.points.splice(index, 1);
		}
	}

	@action.bound
	clearPoints() {
		this.points = [];
	}

	@action.bound
	reset() {
		this.points = [];
		this.isActive = false;
		this.isPaused = false;
	}

	/**
	 * Check if a point already exists at this location (for undo functionality)
	 */
	findPointByLocation(location: google.maps.LatLng, toleranceMeters: number = 1): MeasurementPoint | null {
		return (
			this.points.find((point) => {
				const distance = google.maps.geometry.spherical.computeDistanceBetween(point.location, location);
				return distance < toleranceMeters;
			}) || null
		);
	}

	/**
	 * Find a point by collection revision (for pole clicks)
	 */
	findPointByCollection(collectionRevision: string): MeasurementPoint | null {
		return this.points.find((p) => p.collectionRevision === collectionRevision) || null;
	}

	/**
	 * Get distance between consecutive points (spans)
	 * Returns array of distances in meters
	 */
	@computed
	get spanDistances(): number[] {
		const distances: number[] = [];

		for (let i = 1; i < this.points.length; i++) {
			const prevPoint = this.points[i - 1];
			const currentPoint = this.points[i];

			const prevLoc = {
				latitude: prevPoint.location.lat(),
				longitude: prevPoint.location.lng(),
				altitude: 0,
			};

			const currentLoc = {
				latitude: currentPoint.location.lat(),
				longitude: currentPoint.location.lng(),
				altitude: 0,
			};

			const vector = IkeGeodesic.getVector(prevLoc, currentLoc);
			distances.push(vector.distance);
		}

		return distances;
	}

	/**
	 * Calculate ruling span using the formula: sqrt(sum(distance^3) / totalDistance)
	 * Returns ruling span in meters, or 0 if less than 2 points
	 */
	@computed
	get rulingSpan(): number {
		if (this.points.length < 2) {
			return 0;
		}

		const totalDistance = this.totalDistance;
		if (totalDistance === 0) {
			return 0;
		}

		let numerator = 0;
		for (const distance of this.spanDistances) {
			numerator += Math.pow(distance, 3);
		}

		return Math.sqrt(numerator / totalDistance);
	}

	/**
	 * Calculate total distance across all spans
	 * Returns total distance in meters
	 */
	@computed
	get totalDistance(): number {
		return this.spanDistances.reduce((sum, distance) => sum + distance, 0);
	}
}
